import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { PanelLeftIcon } from "lucide-react";

import { useIsMobile } from "hooks/use-mobile";
import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { useLanguage } from "context/languageContext";
import { translations, isRtlLanguage } from "utils/i18n";

// Ported from shadcn/ui's sidebar block (see arkii-product), trimmed to what
// this app actually uses: a single side="left" sidebar, collapsible="icon"
// on desktop and an off-canvas Sheet on mobile. The "floating"/"inset"
// variants and the sub-menu pieces weren't ported since nothing here needs
// them yet. RTL *is* handled (the app supports Arabic): the fixed/absolute
// pieces below carry `rtl:` counterparts alongside their physical `left`/
// `right` classes, since Tailwind's `rtl:` variant only fires under a
// `dir="rtl"` ancestor and doesn't rewrite the base classes on its own.

const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

const SidebarContext = React.createContext(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
}

function SidebarProvider({ defaultOpen = true, open: openProp, onOpenChange: setOpenProp, className, style, children, ...props }) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);

  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open]
  );

  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((o) => !o) : setOpen((o) => !o);
  }, [isMobile, setOpen]);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        toggleSidebar();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  const state = open ? "expanded" : "collapsed";

  const contextValue = React.useMemo(
    () => ({ state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        style={{ "--sidebar-width": SIDEBAR_WIDTH, "--sidebar-width-icon": SIDEBAR_WIDTH_ICON, ...style }}
        className={cn("group/sidebar-wrapper flex min-h-svh w-full", className)}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  );
}

function SidebarSheetA11yText() {
  const { language } = useLanguage();
  const tr = translations[language].sidebarChrome;
  return (
    <>
      <SheetTitle className="sr-only">{tr.sidebarSrLabel}</SheetTitle>
      <SheetDescription className="sr-only">{tr.mobileSidebarDescription}</SheetDescription>
    </>
  );
}

function Sidebar({ collapsible = "icon", className, children, ...props }) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();
  const { language } = useLanguage();

  if (collapsible === "none") {
    return (
      <div className={cn("flex h-full w-[--sidebar-width] flex-col bg-card text-foreground", className)} {...props}>
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          data-mobile="true"
          className="w-[--sidebar-width] p-0"
          style={{ "--sidebar-width": SIDEBAR_WIDTH_MOBILE }}
          side={isRtlLanguage(language) ? "right" : "left"}
          showCloseButton={false}
        >
          <SidebarSheetA11yText />
          <div className="flex h-full w-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      className="group peer hidden text-foreground md:block"
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
    >
      {/* Sizes the gap in normal flow; the actual sidebar is fixed/overlaid on top of it. */}
      <div
        className={cn(
          "relative w-[--sidebar-width] bg-transparent transition-[width] duration-200 ease-linear",
          "group-data-[collapsible=icon]:w-[--sidebar-width-icon]"
        )}
      />
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-10 hidden h-svh w-[--sidebar-width] border-r border-border transition-[left,width] duration-200 ease-linear md:flex",
          "rtl:left-auto rtl:right-0 rtl:border-r-0 rtl:border-l rtl:transition-[right,width]",
          "group-data-[collapsible=icon]:w-[--sidebar-width-icon]",
          className
        )}
        {...props}
      >
        <div className="flex size-full flex-col bg-card">{children}</div>
      </div>
    </div>
  );
}

function SidebarTrigger({ className, onClick, ...props }) {
  const { toggleSidebar } = useSidebar();
  const { language } = useLanguage();
  const tr = translations[language].sidebarChrome;
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      className={cn(className)}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeftIcon />
      <span className="sr-only">{tr.toggleSidebar}</span>
    </Button>
  );
}

function SidebarRail({ className, ...props }) {
  const { toggleSidebar } = useSidebar();
  const { language } = useLanguage();
  const tr = translations[language].sidebarChrome;
  return (
    <button
      aria-label={tr.toggleSidebar}
      tabIndex={-1}
      onClick={toggleSidebar}
      title={tr.toggleSidebar}
      className={cn(
        "absolute inset-y-0 z-20 hidden w-4 -right-4 cursor-w-resize border-0 bg-transparent p-0 transition-all ease-linear after:absolute after:inset-y-0 after:start-1/2 after:w-[2px] hover:after:bg-border sm:flex",
        "rtl:-left-4 rtl:right-auto rtl:cursor-e-resize",
        "group-data-[collapsible=icon]:cursor-e-resize rtl:group-data-[collapsible=icon]:cursor-w-resize",
        className
      )}
      {...props}
    />
  );
}

function SidebarInset({ className, ...props }) {
  return <main className={cn("relative flex w-full flex-1 flex-col bg-background", className)} {...props} />;
}

function SidebarHeader({ className, ...props }) {
  return <div data-sidebar="header" className={cn("flex flex-col gap-2 p-2", className)} {...props} />;
}

function SidebarFooter({ className, ...props }) {
  return <div data-sidebar="footer" className={cn("flex flex-col gap-2 p-2", className)} {...props} />;
}

function SidebarContent({ className, ...props }) {
  return (
    <div
      data-sidebar="content"
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-0 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className
      )}
      {...props}
    />
  );
}

function SidebarGroup({ className, ...props }) {
  return <div data-sidebar="group" className={cn("relative flex w-full min-w-0 flex-col p-2", className)} {...props} />;
}

function SidebarMenu({ className, ...props }) {
  return <ul data-sidebar="menu" className={cn("flex w-full min-w-0 list-none flex-col gap-1 p-0", className)} {...props} />;
}

function SidebarMenuItem({ className, ...props }) {
  return <li data-sidebar="menu-item" className={cn("group/menu-item relative", className)} {...props} />;
}

const sidebarMenuButtonVariants = cva(
  "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md border-0 bg-transparent p-2 text-start text-sm text-foreground no-underline outline-none transition-[width,height,padding] hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 [&>svg]:size-4 [&>svg]:shrink-0 [&>span:last-child]:truncate",
  {
    variants: {
      isActive: {
        true: "bg-[var(--border-strong)] font-medium text-foreground",
        false: "",
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
      },
    },
    defaultVariants: {
      isActive: false,
      size: "default",
    },
  }
);

const SidebarMenuButton = React.forwardRef(
  ({ asChild = false, isActive = false, size = "default", tooltip, className, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const { isMobile, state } = useSidebar();

    const button = (
      <Comp
        ref={ref}
        data-sidebar="menu-button"
        data-active={isActive}
        className={cn(sidebarMenuButtonVariants({ isActive, size }), className)}
        {...props}
      />
    );

    if (!tooltip) return button;

    const tooltipProps = typeof tooltip === "string" ? { children: tooltip } : tooltip;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="right" align="center" hidden={state !== "collapsed" || isMobile} {...tooltipProps} />
      </Tooltip>
    );
  }
);
SidebarMenuButton.displayName = "SidebarMenuButton";

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
};
